import React from 'react';
import { useScroll } from 'hooks/useScroll';
import {
  ticksToPlotLines,
  ticksToTickInterval,
} from 'components/features/new-report/utils/util';
import { AssetHistoryItemsProps } from 'components/features/home/assetStuffing/AssetStuffing';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { DATE_FORMAT } from 'components/features/new-report/utils/chartTypes';
import { ReportBaseProps } from '../WeeklyReport';
import {
  AssetTransitionChart as AssetTransitionChartInternal,
  AssetTransitionChartProps as AssetTransitionChartProps_,
  createPrevTotalAssetAmount,
  createRawChartData,
  RawChartData,
} from '../../components/AssetTransitionChart/AssetTransitionChart';
import { Chart as Chart_ } from '../../components/AssetTransitionChart/HighChart';

export type AssetTransitionChartProps = AssetTransitionChartProps_;

export const Chart = Chart_;

export const AssetTransitionChart = ({
  Base,
  transitions,
}: {
  Base: ReportBaseProps;
  transitions: AssetHistoryItemsProps[];
}) => {
  const { PreviousTotalAssetAmount } = Base;

  const ticks = transitions
    .map(v => yyyymmddToDate(v.day.toString()) || new Date())
    .sort((a, b) => a.getTime() - b.getTime());

  const xAxisInfo = {
    tickInterval: ticksToTickInterval(ticks, 1),
    plotLines: ticksToPlotLines(ticks),
  };

  const data = {
    dateFormat: DATE_FORMAT.WITH_WEEKDAY,
    prevTotalAssetAmount: createPrevTotalAssetAmount(
      PreviousTotalAssetAmount ?? 0
    ),
    chartData: createRawChartData(
      transitions.map(t => {
        const row: RawChartData[number] = {
          date: yyyymmddToDate(t.day.toString()) || new Date(),
          prevTotalAssetAmount: createPrevTotalAssetAmount(
            PreviousTotalAssetAmount ?? 0
          ),
          Stock:
            t?.stockAmount === null
              ? t.stockAmount
              : t.stockAmount > 0
              ? t.stockAmount
              : 0,
          UnlistedStockAmount:
            t?.unlistedStockAmount === null
              ? t.unlistedStockAmount
              : t.unlistedStockAmount > 0
              ? t.unlistedStockAmount
              : 0,
          InvestmentTrust:
            t?.investmentTrustAmount === null
              ? t.investmentTrustAmount
              : t.investmentTrustAmount > 0
              ? t.investmentTrustAmount
              : 0,
          ForeignStock:
            t?.uSStockAmount === null
              ? t.uSStockAmount
              : t.uSStockAmount > 0
              ? t.uSStockAmount
              : 0,
          EtfEtnAmount:
            t?.etfEtnAmount === null
              ? t.etfEtnAmount
              : t.etfEtnAmount > 0
              ? t.etfEtnAmount
              : 0,
          ForeignEtfEtnAmount:
            t?.foreignEtfEtnAmount === null
              ? t.foreignEtfEtnAmount
              : t.foreignEtfEtnAmount > 0
              ? t.foreignEtfEtnAmount
              : 0,
          ReitAmount:
            t?.reitAmount === null
              ? t.reitAmount
              : t.reitAmount > 0
              ? t.reitAmount
              : 0,
          ForeignReitAmount:
            t?.foreignReitAmount === null
              ? t.foreignReitAmount
              : t.foreignReitAmount > 0
              ? t.foreignReitAmount
              : 0,
          BondAmount:
            t?.bondAmount === null
              ? t.bondAmount
              : t.bondAmount > 0
              ? t.bondAmount
              : 0,
          ForeignBondAmount:
            t?.foreignBondAmount === null
              ? t.foreignBondAmount
              : t.foreignBondAmount > 0
              ? t.foreignBondAmount
              : 0,
          EbAmount:
            t?.ebAmount === null ? t.ebAmount : t.ebAmount > 0 ? t.ebAmount : 0,
          ForeignEbAmount:
            t?.foreignEbAmount === null
              ? t.foreignEbAmount
              : t.foreignEbAmount > 0
              ? t.foreignEbAmount
              : 0,
          RealEstateAmount:
            t?.realEstateAmount === null
              ? t.realEstateAmount
              : t.realEstateAmount > 0
              ? t.realEstateAmount
              : 0,
          RoboAdvisor:
            t?.roboAdvisorAmount === null
              ? t.roboAdvisorAmount
              : t.roboAdvisorAmount > 0
              ? t.roboAdvisorAmount
              : 0,
          Margin:
            t?.marginAmount === null
              ? t.marginAmount
              : t.marginAmount > 0
              ? t.marginAmount
              : 0,
          Cash:
            t?.cashAmount === null
              ? t.cashAmount
              : t.cashAmount > 0
              ? t.cashAmount
              : 0,
          Other:
            t?.otherAmount === null
              ? t.otherAmount
              : t.otherAmount > 0
              ? t.otherAmount
              : 0,
          MarginProfitOrLoss:
            t?.marginProfitOrLoss === null
              ? t.marginProfitOrLoss
              : t.marginProfitOrLoss > 0
              ? t.marginProfitOrLoss
              : 0,
          Incorrect: t?.incorrect,
        };
        return row;
      })
    ),
    xAxisInfo,
    periodVerbose: '先週',
  };
  useScroll();

  return (
    <AssetTransitionChartInternal
      {...{
        periodUnit: '週間',
        Chart,
        data,
      }}
    />
  );
};
